import { Engine, CreateAudioEngineAsync, CreateSoundAsync } from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import './style.css';

import { createScene } from './scene/createScene';
import { createTorus } from './scene/createTorus';
import { createImagePlane } from './scene/createImagePlane';
import { createFloor } from './scene/createFloor';
import { setupWebXR } from './xr/setupWebXR';
import { handleHollaPickAsync } from './audio/handleHollaPickAsync';
import { ensureSessionId } from './utils/cookies';
import { ensureAuth, AuthResult } from './supabase/client';
import { subscribeToBroadcast, unsubscribeFromBroadcast, testBroadcast } from './supabase/mediaSubscription';
import { searchMediaBundle } from './supabase/storageSearch';

async function main(): Promise<void> {
  ensureSessionId();
  const auth: AuthResult = await ensureAuth();
  const userUuid = auth.userId;

  if (auth.isAnonymous) {
    console.log('[Main] Running with anonymous auth');
  } else {
    console.log('[Main] Authenticated as:', auth.email || auth.userId);
  }

  const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
  if (!canvas) {
    throw new Error('Canvas element #renderCanvas not found');
  }

  // Initialize BabylonJS engine
  const engine = new Engine(canvas, true, {
    preserveDrawingBuffer: true,
    stencil: true
  });

  // Create the scene with camera and lighting
  const scene = createScene(engine, canvas);

  // Add AR content: torus closest to user, image plane behind it
  let ambientBirds: { pause(): void; play(): void; isPlaying: boolean } | undefined;

  const hollaSphere = await createTorus(scene, (mesh) => {
    console.log('[Orb] Picked:', mesh.name);
    handleHollaPickAsync(mesh, ambientBirds ? [ambientBirds] : [], userUuid);
  });
  const imagePlane = createImagePlane(scene, 'scenes/poe_stance_on_deepfakes.webm');

  // Create floor mesh as teleportation landing zone
  const floor = createFloor(scene);

  // Setup WebXR AR with teleportation
  const { isSupported } = await setupWebXR(scene, [floor]);

  if (!isSupported) {
    updateStatus('WebXR not supported - viewing in browser mode');
  } else {
    updateStatus('Ready - Tap "Enter AR" button');
  }

  // Create audio engine with unmute button (required for browser autoplay policy)
  await CreateAudioEngineAsync();

  // Style buttons to stay within viewport on all devices (use fixed positioning)
  const buttonStyle = document.createElement('style');
  buttonStyle.appendChild(
    document.createTextNode(`
      #babylonUnmuteButton {
        position: fixed !important;
        top: unset !important;
        bottom: 70px !important;
        left: 10px !important;
        right: unset !important;
      }
      .babylonVRiconButton {
        position: fixed !important;
        bottom: 10px !important;
        right: 10px !important;
      }
    `)
  );
  document.head.appendChild(buttonStyle);

  // Ambient background sound (looped)
  const birdsSound = await CreateSoundAsync('ambientBirds',
    'https://itipjzcggtzxrjxookes.supabase.co/storage/v1/object/public/babylon/sounds/ambient_birds.wav',
    { loop: true, autoplay: true, volume: 0.3 }
  );
  // Cast to include isPlaying (exists at runtime but not in StaticSound types)
  ambientBirds = birdsSound as typeof birdsSound & { isPlaying: boolean };

  // Subscribe to per-user broadcast channel for media updates from n8n
  const broadcastChannel = subscribeToBroadcast(userUuid, async (payload) => {
    if (payload.url) {
      // New media bundle flow: search for all files matching the base name
      console.log('[Main] Searching for media bundle:', payload.url);
      try {
        const bundle = await searchMediaBundle(payload.url);
        if (bundle.video && bundle.audio) {
          console.log('[Main] Playing media bundle:', payload.url);
          await imagePlane.playMediaBundle(bundle);
        } else {
          console.warn('[Main] Media bundle missing required files:', {
            baseName: payload.url,
            hasVideo: !!bundle.video,
            hasAudio: !!bundle.audio,
            files: bundle.files.map(f => f.path)
          });
        }
      } catch (err) {
        console.error('[Main] Failed to search media bundle:', err);
      }
    } else if (payload.video_file && payload.audio_file) {
      // Legacy flow: direct video and audio filenames
      console.log('[Main] Playing media (legacy):', payload.video_file, payload.audio_file);
      await imagePlane.playMedia(payload.video_file, payload.audio_file);
    }
  });

  window.addEventListener('beforeunload', () => {
    unsubscribeFromBroadcast(broadcastChannel);
  });

  // Expose test function for debugging
  (window as unknown as { testBroadcast: () => void }).testBroadcast = () => testBroadcast(broadcastChannel);

  // Render loop
  engine.runRenderLoop(() => {
    hollaSphere.addRotation(0.003, 0.005, 0.0025);
    scene.render();
  });

  // Handle window resize and orientation changes
  const handleResize = () => {
    engine.resize();
  };
  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', () => {
    // Delay resize after orientation change to let viewport settle
    setTimeout(handleResize, 100);
  });

  // Force initial resize to ensure correct sizing
  handleResize();
}

function updateStatus(message: string): void {
  const statusEl = document.getElementById('status');
  if (statusEl) statusEl.textContent = message;
}

main().catch((err) => {
  console.error('Failed to initialize AR experience:', err);
  updateStatus(`Error: ${err.message}`);
});
